import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send, 
  User, 
  Clock,
  CheckCheck,
  Paperclip,
  Smile
} from 'lucide-react';
import Button from '@components/common/Button';
import mentorService from '@services/mentor.service';
import toast from 'react-hot-toast';

const MentorChat = () => {
  const { mentorId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [mentor, setMentor] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchMentorDetails();
    fetchConversation();
  }, [mentorId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMentorDetails = async () => {
    try {
      const response = await mentorService.getMentorById(mentorId);
      if (response.success) {
        setMentor(response.data.user || response.data);
      }
    } catch (error) {
      console.error('Failed to fetch mentor:', error);
      // Mock data
      setMentor({
        id: mentorId,
        firstName: 'Mr. Ajay',
        lastName: 'Patil',
        expertise: 'Expert Mentor',
        rating: 4.5
      });
    }
  };

  const fetchConversation = async () => {
    try {
      setLoading(true);
      const response = await mentorService.getConversation(mentorId);
      
      if (response.success) {    
        setMessages(response?.data?.data?.messages || []);
      }
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
      toast.error('Failed to load conversation');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim()) {
      return;
    }

    const messageText = newMessage.trim();
    setNewMessage('');

    // Optimistic update
    const tempMessage = {
      id: Date.now(),
      text: messageText,
      sender: 'student',
      timestamp: new Date().toISOString(),
      status: 'sending'
    };
    
    setMessages(prev => [...prev, tempMessage]);

    try {
      setSending(true);
      const response = await mentorService.sendMessage({
        mentorId,
        message: messageText
      });

      if (response.success) {
        // Update message status
        setMessages(prev => 
          prev.map(msg => 
            msg.id === tempMessage.id 
              ? { ...msg, id: response.data.messageId, status: 'sent' }
              : msg
          )
        );
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
      // Update status to failed
      setMessages(prev => 
        prev.map(msg => 
          msg.id === tempMessage.id 
            ? { ...msg, status: 'failed' }
            : msg
        )
      );
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-3 p-3 sm:p-4">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>

          {mentor && (
            <>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-secondary-400 to-secondary-600 rounded-full flex items-center justify-center">
                <span className="text-lg sm:text-xl font-bold text-white">
                  {mentor.firstName?.charAt(0) || 'M'}
                </span>
              </div>
              
              <div className="flex-1 min-w-0">
                <h2 className="text-base sm:text-lg font-bold text-primary-700 truncate">
                  {mentor.firstName && mentor.lastName 
                    ? `${mentor.firstName} ${mentor.lastName}` 
                    : 'Mentor'}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {mentor.expertise || 'Expert Mentor'}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500"></div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => {
              const showDate = index === 0 || 
                formatDate(messages[index - 1].timestamp) !== formatDate(message.timestamp);

              return (
                <div key={message.id}>
                  {/* Date Divider */}
                  {showDate && (
                    <div className="flex items-center justify-center my-4">
                      <div className="bg-white px-3 py-1 rounded-full shadow-sm">
                        <span className="text-xs text-gray-600 font-medium">
                          {formatDate(message.timestamp)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Message Bubble */}
                  <div className={`flex ${message.sender === 'student' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] sm:max-w-[60%] ${
                      message.sender === 'student' 
                        ? 'bg-secondary-500 text-white rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl' 
                        : 'bg-white text-gray-800 rounded-tl-2xl rounded-tr-2xl rounded-br-2xl shadow-sm'
                    } px-4 py-2.5`}>
                      <p className="text-sm sm:text-base leading-relaxed break-words">
                        {message.text}
                      </p>
                      <div className={`flex items-center gap-1 mt-1 ${
                        message.sender === 'student' ? 'justify-end' : 'justify-start'
                      }`}>
                        <span className={`text-xs ${
                          message.sender === 'student' ? 'text-white/80' : 'text-gray-500'
                        }`}>
                          {formatTime(message.timestamp)}
                        </span>
                        {message.sender === 'student' && (
                          <span>
                            {message.status === 'sending' && (
                              <Clock className="w-3 h-3 text-white/60" />
                            )}
                            {message.status === 'sent' && (
                              <CheckCheck className="w-3.5 h-3.5 text-white/80" />
                            )}
                            {message.status === 'read' && (
                              <CheckCheck className="w-3.5 h-3.5 text-blue-300" />
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-gray-200 p-3 sm:p-4">
        <form onSubmit={handleSendMessage} className="flex items-end gap-2">
          <div className="flex-1 bg-gray-100 rounded-2xl px-4 py-2 flex items-center gap-2">
            <button
              type="button"
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <Smile className="w-5 h-5" />
            </button>
            
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              placeholder="Type your question..."
              className="flex-1 bg-transparent border-none outline-none resize-none text-sm sm:text-base max-h-32 py-1"
              rows="1"
              style={{ minHeight: '24px' }}
            />

            <button
              type="button"
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <Paperclip className="w-5 h-5" />
            </button>
          </div>

          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all ${
              newMessage.trim() && !sending
                ? 'bg-secondary-500 hover:bg-secondary-600 text-white shadow-lg hover:shadow-xl'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default MentorChat;
